import { useEffect, useState } from 'react'
import { api, type JoinResponse } from '../lib/api'
import { device } from '../lib/device'
import type { Me } from '../lib/types'
import { useToast } from './Toast'

interface Props {
  onJoined: (me: Me) => void
  initialError?: string | null
}

type Tab = 'create' | 'code'

function randomCode() {
  let s = ''
  for (let i = 0; i < 8; i++) s += Math.floor(Math.random() * 10)
  return s
}

function mapJoin(resp: JoinResponse): Me {
  return {
    family: resp.family,
    device: resp.device,
    devices: resp.devices,
    ai_configured: resp.ai_configured,
    ai_vision_configured: resp.ai_vision_configured,
    photos_enabled: resp.photos_enabled,
    r2_usage_bytes: resp.r2_usage_bytes,
  }
}

export default function JoinScreen({ onJoined, initialError }: Props) {
  const toast = useToast()
  const [tab, setTab] = useState<Tab>('create')
  const [error, setError] = useState<string | null>(initialError || null)

  // 创建 tab
  const [createFamilyName, setCreateFamilyName] = useState('')
  const [createRelationship, setCreateRelationship] = useState<'father' | 'mother' | 'other' | null>(null)
  const [createOtherName, setCreateOtherName] = useState('')
  const [createCode, setCreateCode] = useState<string>(randomCode())
  const [codeTaken, setCodeTaken] = useState(false)
  const [checkingCode, setCheckingCode] = useState(false)
  const [creating, setCreating] = useState(false)

  // 代码加入 tab
  const [lookupCode, setLookupCode] = useState('')
  const [lookupResult, setLookupResult] = useState<{ family_name: string; member_count: number } | null>(null)
  const [joinFamilyName, setJoinFamilyName] = useState('')
  const [joinRelationship, setJoinRelationship] = useState<'father' | 'mother' | 'other' | null>(null)
  const [joinOtherName, setJoinOtherName] = useState('')
  const [looking, setLooking] = useState(false)
  const [joining, setJoining] = useState(false)

  // 创建成功后的 modal（同时缓存 resp 用来 enter）
  const [createdResp, setCreatedResp] = useState<JoinResponse | null>(null)

  // 手动输入 code 时查重
  useEffect(() => {
    if (!/^\d{8}$/.test(createCode)) {
      setCodeTaken(false)
      return
    }
    setCheckingCode(true)
    const t = setTimeout(async () => {
      try {
        const r = await api.lookupFamily(createCode)
        setCodeTaken(true) // 查到了说明被占用
      } catch (e) {
        // 404 = 不存在 = 可用
        setCodeTaken(false)
      } finally {
        setCheckingCode(false)
      }
    }, 400)
    return () => clearTimeout(t)
  }, [createCode])

  function applyJoin(resp: JoinResponse) {
    device.setSession({
      deviceId: resp.device.id,
      token: resp.device_token,
      familyId: resp.family.id,
      deviceName: resp.device.name,
    })
    onJoined(mapJoin(resp))
  }

  async function createFamily() {
    const familyName = createFamilyName.trim()
    if (!familyName) {
      setError('请给家庭起个名字')
      return
    }
    if (!createRelationship) {
      setError('请选择你跟宝宝的关系')
      return
    }
    let deviceName = ''
    if (createRelationship === 'father') deviceName = '爸爸'
    else if (createRelationship === 'mother') deviceName = '妈妈'
    else deviceName = createOtherName.trim()
    if (!deviceName) {
      setError('请填你的称呼')
      return
    }
    if (!/^\d{8}$/.test(createCode)) {
      setError('家庭代码必须是 8 位数字')
      return
    }
    if (codeTaken) {
      setError('这个代码已被使用，请换一个')
      return
    }
    setCreating(true)
    setError(null)
    try {
      device.setDeviceName(deviceName)
      const resp = await api.createFamily({
        familyName,
        relationship: createRelationship,
        deviceName: createRelationship === 'other' ? deviceName : undefined,
        joinCode: createCode,
      })
      // 弹 modal 展示 join_code，关闭后才进主页
      setCreatedResp(resp)
    } catch (e) {
      const msg = e instanceof Error ? e.message : '创建失败'
      if (msg.includes('已被使用') || msg.includes('CODE_TAKEN') || msg.includes('409')) {
        setCodeTaken(true)
      }
      setError(msg)
    } finally {
      setCreating(false)
    }
  }

  function closeCreatedModalAndEnter() {
    if (!createdResp) return
    applyJoin(createdResp)
    setCreatedResp(null)
  }

  async function lookupFamily() {
    const code = lookupCode.trim()
    if (!/^\d{8}$/.test(code)) {
      setError('代码必须是 8 位数字')
      return
    }
    setLooking(true)
    setError(null)
    setLookupResult(null)
    try {
      const r = await api.lookupFamily(code)
      setLookupResult({ family_name: r.family_name, member_count: r.member_count })
    } catch (e) {
      const msg = e instanceof Error ? e.message : '代码不存在'
      setError(msg)
    } finally {
      setLooking(false)
    }
  }

  async function joinByCode() {
    const code = lookupCode.trim()
    if (!/^\d{8}$/.test(code) || !lookupResult) return
    const familyName = joinFamilyName.trim() || undefined
    const relationship = joinRelationship || 'other'
    let deviceName = ''
    if (joinRelationship === 'father') deviceName = '爸爸'
    else if (joinRelationship === 'mother') deviceName = '妈妈'
    else if (joinRelationship === 'other') deviceName = joinOtherName.trim()
    if (!deviceName) {
      setError('请填你的称呼')
      return
    }
    setJoining(true)
    setError(null)
    try {
      device.setDeviceName(deviceName)
      const resp = await api.joinFamily({
        code,
        familyName,
        relationship,
        deviceName: joinRelationship === 'other' ? deviceName : undefined,
      })
      applyJoin(resp)
      toast.show('已加入家庭')
    } catch (e) {
      setError(e instanceof Error ? e.message : '加入失败')
    } finally {
      setJoining(false)
    }
  }

  return (
    <div className="join-screen">
      <div className="join-container">
        <h1>宝宝记录</h1>

        <div className="join-tabs">
          <button
            type="button"
            className={`join-tab ${tab === 'create' ? 'active' : ''}`}
            onClick={() => { setTab('create'); setError(null) }}
          >
            创建
          </button>
          <button
            type="button"
            className={`join-tab ${tab === 'code' ? 'active' : ''}`}
            onClick={() => { setTab('code'); setError(null) }}
          >
            输入代码
          </button>
        </div>

        {tab === 'create' && (
          <>
            <p className="hint">给家庭起个名字</p>
            <input
              value={createFamilyName}
              onChange={(e) => setCreateFamilyName(e.target.value)}
              placeholder="例如：小明的家"
              maxLength={30}
            />

            <p className="hint" style={{ marginTop: 4 }}>你跟宝宝的关系</p>
            <div className="relationship-picker">
              {(['father', 'mother', 'other'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`relationship-pill ${createRelationship === r ? 'active' : ''}`}
                  onClick={() => setCreateRelationship(r)}
                >
                  {r === 'father' ? '👨 爸爸' : r === 'mother' ? '👩 妈妈' : '其他'}
                </button>
              ))}
            </div>
            {createRelationship === 'other' && (
              <input
                value={createOtherName}
                onChange={(e) => setCreateOtherName(e.target.value)}
                placeholder="例如：奶奶 / 小姨 / 舅舅"
                maxLength={20}
                style={{ marginTop: 8 }}
              />
            )}

            <div className="join-code-pick">
              <div className="join-code-label">
                家庭代码（别人用它加入你的家庭）
              </div>
              <div className="join-code-input-row">
                <input
                  inputMode="numeric"
                  pattern="\d{8}"
                  value={createCode}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 8)
                    setCreateCode(v)
                    setCodeTaken(false)
                  }}
                  placeholder="8 位数字"
                  maxLength={8}
                  className={`join-code-input ${codeTaken ? 'taken' : ''}`}
                />
                <button
                  type="button"
                  className="join-code-action"
                  onClick={() => {
                    setCreateCode(randomCode())
                    setCodeTaken(false)
                  }}
                  title="随机生成"
                >
                  🎲
                </button>
              </div>
              {createCode.length === 8 && (
                <div className={`join-code-hint ${codeTaken ? 'bad' : 'ok'}`}>
                  {checkingCode ? '检查中...' : codeTaken ? '已被占用，请换一个' : '✓ 这个代码可用'}
                </div>
              )}
            </div>
            <button
              className="btn-primary"
              onClick={createFamily}
              disabled={creating || codeTaken || createCode.length !== 8}
            >
              {creating ? '处理中...' : '创建新家庭'}
            </button>
          </>
        )}

        {tab === 'code' && (
          <>
            <p className="hint">输入家庭代码（家人告诉你）</p>
            <input
              inputMode="numeric"
              value={lookupCode}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 8)
                setLookupCode(v)
                setLookupResult(null)
              }}
              placeholder="8 位数字"
              maxLength={8}
              className="join-code-input join-code-input-big"
              style={{ textAlign: 'center', fontSize: 22, letterSpacing: 4, fontWeight: 700 }}
            />
            <button
              className="btn-primary"
              onClick={lookupFamily}
              disabled={looking || lookupCode.length !== 8}
            >
              {looking ? '查询中...' : lookupResult ? '重新查询' : '查询'}
            </button>
            {lookupResult && (
              <div className="lookup-result">
                <div className="lookup-family-name">「{lookupResult.family_name}」</div>
                <div className="lookup-meta">{lookupResult.member_count} 位成员</div>

                <p className="hint" style={{ marginTop: 12 }}>家庭名字（可选，不填就用上面那个）</p>
                <input
                  value={joinFamilyName}
                  onChange={(e) => setJoinFamilyName(e.target.value)}
                  placeholder="例如：奶奶家"
                  maxLength={30}
                />

                <p className="hint" style={{ marginTop: 8 }}>你跟宝宝的关系（可选）</p>
                <div className="relationship-picker">
                  <button
                    type="button"
                    className={`relationship-pill ${joinRelationship === 'father' ? 'active' : ''}`}
                    onClick={() => setJoinRelationship(joinRelationship === 'father' ? null : 'father')}
                  >
                    👨 爸爸
                  </button>
                  <button
                    type="button"
                    className={`relationship-pill ${joinRelationship === 'mother' ? 'active' : ''}`}
                    onClick={() => setJoinRelationship(joinRelationship === 'mother' ? null : 'mother')}
                  >
                    👩 妈妈
                  </button>
                  <button
                    type="button"
                    className={`relationship-pill ${joinRelationship === 'other' ? 'active' : ''}`}
                    onClick={() => setJoinRelationship(joinRelationship === 'other' ? null : 'other')}
                  >
                    其他
                  </button>
                </div>
                {joinRelationship === 'other' && (
                  <input
                    value={joinOtherName}
                    onChange={(e) => setJoinOtherName(e.target.value)}
                    placeholder="例如：奶奶 / 小姨 / 舅舅"
                    maxLength={20}
                    style={{ marginTop: 8 }}
                  />
                )}

                <button
                  className="btn-primary"
                  onClick={joinByCode}
                  disabled={joining || (joinRelationship === 'other' && !joinOtherName.trim())}
                  style={{ marginTop: 12 }}
                >
                  {joining ? '加入中...' : '加入家庭'}
                </button>
              </div>
            )}
          </>
        )}

        {error && <p className="error">{error}</p>}
      </div>

      {createdResp && (
        <CreatedFamilyModal
          info={{
            join_code: createdResp.family.join_code || '?',
            device_name: createdResp.device.name,
            family_name: createdResp.family.name,
          }}
          onClose={closeCreatedModalAndEnter}
        />
      )}
    </div>
  )
}

/**
 * 创建家庭成功后的 modal
 * 展示 join_code + 截图提示 + 复制按钮
 */
function CreatedFamilyModal({
  info,
  onClose,
}: {
  info: { join_code: string; device_name: string; family_name: string }
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  function copy() {
    try {
      navigator.clipboard.writeText(info.join_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }
  return (
    <div className="overlay" onClick={onClose}>
      <div className="overlay-content created-modal" onClick={(e) => e.stopPropagation()}>
        <div className="created-modal-icon">🎉</div>
        <h2 className="created-modal-title">家庭已创建</h2>
        <p className="created-modal-hint">
          <strong>{info.family_name}</strong> · 你是 <strong>{info.device_name}</strong>
        </p>
        <div className="created-modal-code-block">
          <div className="created-modal-code-label">家庭代码（家人用这个加入）</div>
          <div className="created-modal-code">{info.join_code}</div>
          <button className="btn-secondary" onClick={copy}>
            {copied ? '✓ 已复制' : '📋 复制代码'}
          </button>
        </div>
        <p className="created-modal-warn">
          ⚠️ 截图保存。下次换设备 / 换浏览器，在「输入代码」那里填这个码就能进。
          码也会一直显示在「我的」页面。
        </p>
        <button className="btn-primary" onClick={onClose}>
          我已保存，进入主页
        </button>
      </div>
    </div>
  )
}
